/**
 * AppraisalTabsCustomiser
 *
 * Iter182++ — operator-customisable tab order + labels for the
 * AppraisalDetailPage. Persists per-browser via localStorage so each
 * operator can flow through appraisals in the order that matches
 * their workflow (e.g. Reroof spec → Components → Quote Calc, vs.
 * Inspection → Roof Appraisal → Quote).
 *
 * Storage shape (localStorage key: `rtr.appraisal.tabs.v1`):
 *   {
 *     order:  ["Overview", "Roof Appraisal", ...],  // unknown keys
 *                                                    // appended at end
 *     labels: { "Roof Details": "Inspection Notes" }, // custom rename
 *     hidden: ["Work Sheets"],   // hide tabs you never use
 *   }
 *
 * The original tab KEYS never change — the page's `tab === 'Foo'`
 * dispatch still works regardless of relabel. Only what the operator
 * sees changes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowUp, ArrowDown, RotateCcw, Eye, EyeOff, GripVertical } from 'lucide-react';

const LS_KEY = 'rtr.appraisal.tabs.v1';

/** Hook — returns { order, labels, hidden, persist, reset } */
export function useAppraisalTabsConfig(defaultTabs) {
  const [config, setConfig] = useState(() => loadConfig(defaultTabs));

  // Re-sync if the defaults list changes (e.g. a new tab is added in
  // a future iteration). New keys are appended; removed keys filtered.
  useEffect(() => {
    setConfig((c) => mergeWithDefaults(c, defaultTabs));
  }, [defaultTabs]);

  const persist = (next) => {
    setConfig(next);
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* quota / disabled */ }
  };
  const reset = () => {
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
    setConfig({ order: [...defaultTabs], labels: {}, hidden: [] });
  };
  return { ...config, persist, reset };
}

function loadConfig(defaultTabs) {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { order: [...defaultTabs], labels: {}, hidden: [] };
    const parsed = JSON.parse(raw);
    return mergeWithDefaults({
      order:  Array.isArray(parsed.order)  ? parsed.order  : [...defaultTabs],
      labels: parsed.labels && typeof parsed.labels === 'object' ? parsed.labels : {},
      hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
    }, defaultTabs);
  } catch {
    return { order: [...defaultTabs], labels: {}, hidden: [] };
  }
}

function mergeWithDefaults(cfg, defaultTabs) {
  // Keep operator's saved order, then append any new tabs added to the
  // codebase since their last visit; drop any keys removed by a refactor.
  const validSaved = (cfg.order || []).filter((k) => defaultTabs.includes(k));
  const newKeys    = defaultTabs.filter((k) => !validSaved.includes(k));
  return {
    order: [...validSaved, ...newKeys],
    labels: cfg.labels || {},
    hidden: (cfg.hidden || []).filter((k) => defaultTabs.includes(k)),
  };
}

/** Helper — returns the list of {key, label, hidden} in operator order. */
export function buildTabList(config, defaultTabs) {
  const merged = mergeWithDefaults(config, defaultTabs);
  return merged.order.map((key) => ({
    key,
    label: merged.labels[key] || key,
    hidden: merged.hidden.includes(key),
  }));
}

export default function AppraisalTabsCustomiser({ open, onClose, defaultTabs, config, onPersist, onReset }) {
  // Local working copy so the operator can cancel without breaking
  // anything. Committed on Save.
  const [draft, setDraft] = useState(() => structuredClone(config));
  useEffect(() => {
    if (open) setDraft(structuredClone(config));
  }, [open, config]);

  const ordered = useMemo(() => buildTabList(draft, defaultTabs), [draft, defaultTabs]);

  if (!open) return null;

  const move = (idx, delta) => {
    const next = [...draft.order];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setDraft({ ...draft, order: next });
  };
  const setLabel = (key, label) => {
    const labels = { ...draft.labels };
    if (label && label.trim() && label.trim() !== key) labels[key] = label.trim();
    else delete labels[key];
    setDraft({ ...draft, labels });
  };
  const toggleHidden = (key) => {
    const set = new Set(draft.hidden);
    if (set.has(key)) set.delete(key); else set.add(key);
    setDraft({ ...draft, hidden: Array.from(set) });
  };
  const handleSave = () => { onPersist(draft); onClose(); };
  const handleResetDraft = () => { setDraft({ order: [...defaultTabs], labels: {}, hidden: [] }); };

  return (
    <div
      data-testid="tabs-customiser-modal"
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <GripVertical size={16} className="text-slate-400" /> Customise appraisal tabs
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Reorder tabs, rename them, or hide ones you never use. Saved to this browser only.
            </p>
          </div>
          <button
            data-testid="tabs-customiser-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          ><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-auto p-3">
          <ol className="space-y-1.5">
            {ordered.map((t, idx) => (
              <li
                key={t.key}
                data-testid={`tabs-customiser-row-${t.key.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-2 px-2.5 py-2 rounded border transition-colors ${
                  t.hidden ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <span className="font-mono text-[10px] text-slate-400 w-6 text-right">#{idx + 1}</span>
                <button
                  type="button"
                  disabled={idx === 0}
                  onClick={() => move(idx, -1)}
                  className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  title="Move up"
                ><ArrowUp size={13} /></button>
                <button
                  type="button"
                  disabled={idx === ordered.length - 1}
                  onClick={() => move(idx, 1)}
                  className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  title="Move down"
                ><ArrowDown size={13} /></button>
                <div className="flex-1 min-w-0 grid grid-cols-[1fr_1fr] gap-2 items-center">
                  <span className="text-[11px] text-slate-500 truncate" title={`Original key: ${t.key}`}>{t.key}</span>
                  <input
                    type="text"
                    value={draft.labels[t.key] || ''}
                    placeholder={t.key}
                    onChange={(e) => setLabel(t.key, e.target.value)}
                    className="text-xs px-2 py-1 border border-slate-200 rounded focus:border-[#D35400] focus:outline-none"
                    title="Custom label (leave blank to use the default)"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => toggleHidden(t.key)}
                  className={`p-1 rounded ${t.hidden ? 'text-slate-400 hover:bg-slate-100' : 'text-sky-600 hover:bg-sky-50'}`}
                  title={t.hidden ? 'Show this tab' : 'Hide this tab'}
                >{t.hidden ? <EyeOff size={13} /> : <Eye size={13} />}</button>
              </li>
            ))}
          </ol>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-2 bg-slate-50">
          <button
            type="button"
            onClick={handleResetDraft}
            className="text-xs px-2.5 py-1.5 rounded border border-amber-300 bg-white text-amber-700 hover:bg-amber-50 flex items-center gap-1"
            title="Restore the default order, names and visibility"
          ><RotateCcw size={11} /> Reset to default</button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            >Cancel</button>
            <button
              data-testid="tabs-customiser-save"
              type="button"
              onClick={handleSave}
              className="text-xs px-4 py-1.5 rounded bg-[#D35400] text-white font-bold hover:bg-[#A04000]"
            >Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
